import { colors } from '@ardoq/design-tokens';
import { Icon, IconName, IconSize } from '@ardoq/icons';
import { useEffect, useState } from 'react';
import { UnstyledButton } from './Button';

type CopyToClipboardProps = {
  tooltipText: string;
  copyText: string;
  getCopyHtml?: () => string;
  className?: string;
  timeout?: number;
  style?: React.CSSProperties;
  onCopy?: () => void;
};

const writeToClipboard = (text: string, html?: string): Promise<void> => {
  if (!html || typeof ClipboardItem === 'undefined') {
    return navigator.clipboard.writeText(text);
  }
  return navigator.clipboard.write([
    new ClipboardItem({
      'text/html': new Blob([html], { type: 'text/html' }),
      'text/plain': new Blob([text], { type: 'text/plain' }),
    }),
  ]);
};

export const CopyToClipboard = ({
  tooltipText,
  copyText,
  getCopyHtml,
  className,
  timeout = 500,
  style,
  onCopy,
}: CopyToClipboardProps) => {
  const [isCopied, setIsCopied] = useState(false);

  useEffect(() => {
    if (isCopied) {
      const timeOut = setTimeout(() => setIsCopied(false), timeout);
      return () => {
        if (timeOut) clearTimeout(timeOut);
      };
    }
  }, [isCopied, timeout]);

  const handleCopyUrl = async (): Promise<void> => {
    await writeToClipboard(copyText, getCopyHtml?.());
    setIsCopied(true);
    onCopy?.();
  };

  return (
    <UnstyledButton
      type="button"
      onClickCapture={evt => {
        /** AG grid requires propagation to be stopped in the capture phase
         * @see https://github.com/ag-grid/ag-grid/issues/8528#issuecomment-2285984117
         */
        evt.stopPropagation();

        /** As the event won't reach the onClick with the propagation stopped, we need to call it here */
        handleCopyUrl();
      }}
      data-tooltip-text={isCopied ? undefined : tooltipText}
      aria-label={tooltipText}
      className={className}
      style={{
        display: 'flex',
        alignItems: 'center',
        ...(style ? style : {}),
      }}
    >
      <Icon
        iconName={isCopied ? IconName.CHECK_MARK : IconName.COPY}
        iconSize={IconSize.SMALL}
        color={colors.icon}
      />
    </UnstyledButton>
  );
};
